import { CompanyFindDto, type CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { ContragentFindDto, type ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { ContractFindDto, type ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import {
  DebtCollectionTaskEventFindDto,
  type DebtCollectionTaskEventFindInterface,
} from '@zaoblako/shared/modules/debts/dtos/debt-collection-task-event/debt-collection-task-event-find.dto';
import { ReminderFindDto, type ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import { DebtCollectionTaskStatusEnum } from '@zaoblako/shared/modules/debts/enums/debt-collection-task-status.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DebtsDtoKeysEnum } from '@zaoblako/shared/modules/debts/enums/debts-dto-keys.enum';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';

import * as yup from 'yup';

/** Debt collection task response body - DTO signature */
export interface DebtCollectionTaskFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;

  // Tech fields
  /** Task creation date and time */
  created: Date;
  /** Task last update date and time */
  updated: Date;
  /** Task soft deletion date and time */
  deleted?: Date;

  // Data fields
  /** Debt amount */
  amount: number;
  /** The company collecting the debt */
  company: CompanyFindInterface;
  /** The counterparty from whom the debt is collected */
  contragent: ContragentFindInterface;
  /** The contract under which the debt is collected */
  contract: ContractFindInterface;

  // Deadline fields
  /** Deadline for debt collection */
  deadline?: Date;
  /** Overdue task marker. Calculated automatically. */
  expired?: boolean;

  // Status fields
  /** Current task status */
  status: DebtCollectionTaskStatusEnum;
  /** The date and time when the task entered the {@link DebtCollectionTaskStatusEnum.COMPLETED} status. Calculated automatically. */
  completedAt?: Date;

  // Access control fields
  /** Task creator */
  createdBy?: UserFindInterface;
  /** Responsible user */
  responsible?: UserFindInterface;
  /** Task auditor users */
  userAuditors?: UserFindInterface[];
  /** Task auditor user groups */
  userGroupAuditors?: UserGroupFindInterface[];

  // Other fields
  /** Task events */
  events?: DebtCollectionTaskEventFindInterface[];
  /** Related messenger thread (for comments) */
  thread?: ThreadFindInterface;
  /** Personal reminder for current authorized user */
  reminder?: ReminderFindInterface;
  /** Is task favorite for current authorized user */
  isFavorite?: boolean;
}

/** Debt collection task response body - DTO schema */
export const DebtCollectionTaskFindDto = SerdeDtoClass.create<DebtCollectionTaskFindInterface>(DebtsDtoKeysEnum.DEBT_COLLECTION_TASK_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Дата создания',
    serializer: yup.date(),
  },
  updated: {
    label: 'Дата последнего изменения',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  amount: {
    sortable: true,
    label: 'Сумма',
    serializer: yup.number(),
  },
  company: {
    sortable: true,
    nested: CompanyFindDto,
    label: 'Компания',
    serializer: (_, nested) => nested,
  },
  contragent: {
    sortable: true,
    nested: ContragentFindDto,
    label: 'Контрагент',
    serializer: (_, nested) => nested,
  },
  contract: {
    sortable: true,
    nested: ContractFindDto,
    label: 'Договор',
    serializer: (_, nested) => nested,
  },
  deadline: {
    optional: true,
    sortable: true,
    label: 'Дедлайн',
    serializer: yup.date(),
  },
  expired: {
    optional: true,
    label: 'Просрочена',
    serializer: yup.boolean(),
  },
  status: {
    label: 'Статус',
    serializer: yup
      .string()
      .oneOf(Object.values(DebtCollectionTaskStatusEnum)),
  },
  completedAt: {
    optional: true,
    sortable: true,
    label: 'Завершена',
    serializer: yup.date(),
  },
  createdBy: {
    optional: true,
    sortable: true,
    nested: UserFindDto,
    label: 'Автор',
    serializer: (_, nested) => nested,
  },
  responsible: {
    optional: true,
    sortable: true,
    nested: UserFindDto,
    label: 'Ответственный',
    serializer: (_, nested) => nested,
  },
  userAuditors: {
    optional: true,
    nested: UserFindDto,
    label: 'Пользователи-аудиторы',
    serializer: (_, nested) => yup.array(nested),
  },
  userGroupAuditors: {
    optional: true,
    nested: UserGroupFindDto,
    label: 'Группы-аудиторы',
    serializer: (_, nested) => yup.array(nested),
  },
  events: {
    optional: true,
    label: 'События',
    nested: DebtCollectionTaskEventFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  thread: {
    optional: true,
    nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
    label: 'Ветка комментариев',
    serializer: (_, nested) => nested,
  },
  reminder: {
    optional: true,
    nested: ReminderFindDto,
    label: 'Напоминание',
    serializer: (_, nested) => nested,
  },
  isFavorite: {
    optional: true,
    label: 'В избранном',
    serializer: yup.boolean(),
  },
});
